import { useNavigate, useParams } from "#src/Routers/hooks";
import { FormSchemasTableFilterArea } from "#src/batteries-included-components/FilterAreas/FormsFilterAreas";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useListFormSchemas } from "#src/components/hooks/adapters/useFormSchema";
import { useStickyState } from "#src/hooks/useStickyState";
import { linkToFormTemplateDetail } from "#src/routes/forms/categories/[categoryId]/templates/[formTemplateId]";
import {
  DateDataDisplay,
  EmptyState,
  Flex,
  GridCard,
  Pagination,
  Panel,
  Pill,
  Skeleton,
  StorageKeys,
  type PillProps,
} from "@validereinc/common-components";
import { FormSchemaAdapter } from "@validereinc/domain";
import { DateFormats } from "@validereinc/utilities";
import React from "react";

const { Container, Item } = Flex;

export const FormTemplatesPanel = ({
  withFormCategory,
  filterConfigStorageKey,
  tableStateStorageKey,
}: { withFormCategory?: boolean } & Pick<
  StorageKeys,
  "filterConfigStorageKey" | "tableStateStorageKey"
>) => {
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();
  const [filters] = useStickyState<{
    name?: string;
  }>({}, filterConfigStorageKey);

  const [pagination, setPagination] = useTableSortingAndPagination(
    {
      pageSize: 30,
      pageSizeText: "cards per page",
    },
    filters,
    tableStateStorageKey
  );

  const { name, ...restFilters } = filters ?? {};

  const queryPayload: Parameters<typeof FormSchemaAdapter.getList>[0] = {
    page: pagination.page,
    pageSize: pagination.pageSize,
    filters: {
      status: "active",
      "form_category.id": categoryId,
      ...restFilters,
      ...(name !== undefined ? { name: { $like: name } } : ({} as any)),
      isAlreadyFormatted: true,
    },
  };

  const query = useListFormSchemas(queryPayload, {
    enabled: Boolean(categoryId),
  });

  const onCardClick = (formCategoryId: string, formSchemaId: string) => {
    navigate({
      pathname: linkToFormTemplateDetail(formCategoryId, formSchemaId),
    });
  };

  return (
    <Panel
      title="Templates"
      loaded={!query.isLoading}
      actionRow={[
        <FormSchemasTableFilterArea
          key="filters"
          filterConfigStorageKey={filterConfigStorageKey}
          filterDrawerContentSlot={null}
        />,
      ]}
    >
      {query?.data?.data?.length === 0 ? (
        <EmptyState
          title="There are no form templates to display"
          suggestion="Please try adjusting your filters"
        />
      ) : (
        <>
          <Container variant="grid">
            {query.data?.data?.map(
              ({
                id,
                name,
                updated_at,
                description,
                form_category_id,
                form_category: { name: formCategoryName },
              }) => (
                <Item key={`form-template-${id}`}>
                  <GridCard.Container
                    title={name}
                    onClick={() => onCardClick(form_category_id, id)}
                    isLoading={query?.isLoading}
                    description={description}
                    slotFooter={(props) => (
                      <GridCard.Footer
                        {...props}
                        slotLeft={(props) => (
                          <Skeleton
                            isLoading={props.isLoading}
                            isBusy={props.isBusy}
                          >
                            <p {...props}>
                              Last updated on&nbsp;
                              <DateDataDisplay
                                as="span"
                                isInline
                                value={updated_at}
                                displayFormat={DateFormats.DATE}
                              />
                            </p>
                          </Skeleton>
                        )}
                        slotRight={(props) => (
                          <Skeleton
                            isLoading={props.isLoading}
                            isBusy={props.isBusy}
                          >
                            <div {...props}>
                              {(withFormCategory
                                ? ([
                                    {
                                      variant: "default",
                                      children: formCategoryName,
                                    },
                                  ] satisfies PillProps[])
                                : []
                              ).map((p) => (
                                <Pill
                                  key={p.children}
                                  {...p}
                                />
                              ))}
                            </div>
                          </Skeleton>
                        )}
                      />
                    )}
                  />
                </Item>
              )
            )}
          </Container>

          <Pagination
            {...pagination}
            total={query.data?.total_entries}
            onChange={setPagination}
            style={{ marginTop: 8 }}
          />
        </>
      )}
    </Panel>
  );
};
